/* eslint-disable react/jsx-curly-newline */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/require-param-description */
/* eslint-disable no-else-return */
/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { supabase } from "../../../../../supabase";

const useStyles = makeStyles(theme => ({
  formContainer: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  note: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  supplierItem: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedCount: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

/**
 * SupplierForm component for selecting suppliers and product types
 * This is an optional step in user creation
 * @param root0
 * @param root0.formData
 * @param root0.onSave
 * @param root0.onBack
 * @param root0.onNext
 * @param root0.submitLabel
 */
export default function SupplierForm({
  formData,
  onSave,
  onBack,
  onNext,
  submitLabel = "Save & Continue",
}) {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data from API
  const [productTypes, setProductTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [productTypeSuppliers, setProductTypeSuppliers] = useState([]);

  // Selected items
  const [selectedProductTypes, setSelectedProductTypes] = useState(
    formData.selectedProductTypes || []
  );
  const [selectedSuppliers, setSelectedSuppliers] = useState(
    formData.selectedSuppliers || []
  );

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Update available suppliers when product types change
  useEffect(() => {
    updateAvailableSuppliers();
  }, [selectedProductTypes, productTypeSuppliers, suppliers]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch product types
      const { data: productTypesData, error: ptError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("refs_product_type")
        .select("id, name")
        .order("name");

      if (ptError) throw ptError;

      // Fetch active suppliers
      const { data: suppliersData, error: supError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_master")
        .select("id, supplier_name")
        .eq("is_deleted", false)
        .order("supplier_name");

      if (supError) throw supError;

      // Fetch product type to supplier mappings
      const { data: ptSupplierData, error: ptsError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_product_type_index_view")
        .select("refs_product_type_id, suppliers_master_id");

      if (ptsError) throw ptsError;

      setProductTypes(productTypesData || []);
      setSuppliers(suppliersData || []);
      setProductTypeSuppliers(ptSupplierData || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load suppliers and product types");
    } finally {
      setLoading(false);
    }
  };

  const updateAvailableSuppliers = () => {
    // When product types are deselected, remove associated suppliers
    if (selectedProductTypes.length === 0) {
      setSelectedSuppliers([]);
      return;
    }

    // Get suppliers available for selected product types
    const availableSupplierIds = productTypeSuppliers
      .filter(pts => selectedProductTypes.includes(pts.refs_product_type_id))
      .map(pts => pts.suppliers_master_id);

    // Remove suppliers that are no longer available
    const validSuppliers = selectedSuppliers.filter(supplierId =>
      availableSupplierIds.includes(supplierId)
    );

    if (validSuppliers.length !== selectedSuppliers.length) {
      setSelectedSuppliers(validSuppliers);
    }
  };

  // Get suppliers available for selected product types
  const getAvailableSuppliers = () => {
    if (selectedProductTypes.length === 0) {
      return [];
    }

    const availableSupplierIds = productTypeSuppliers
      .filter(pts => selectedProductTypes.includes(pts.refs_product_type_id))
      .map(pts => pts.suppliers_master_id)
      .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

    return suppliers.filter(supplier =>
      availableSupplierIds.includes(supplier.id)
    );
  };

  // Handle product type selection
  const handleProductTypeChange = (productTypeId, isChecked) => {
    if (isChecked) {
      setSelectedProductTypes(prev => [...prev, productTypeId]);
    } else {
      setSelectedProductTypes(prev => prev.filter(id => id !== productTypeId));
    }
  };

  // Handle supplier selection
  const handleSupplierChange = (supplierId, isChecked) => {
    if (isChecked) {
      setSelectedSuppliers(prev => [...prev, supplierId]);
    } else {
      setSelectedSuppliers(prev => prev.filter(id => id !== supplierId));
    }
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();

    const data = {
      ...formData,
      selectedProductTypes,
      selectedSuppliers,
    };

    if (onSave) {
      onSave(data);
    }

    // Move to next step
    if (onNext) {
      onNext(data);
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const availableSuppliers = getAvailableSuppliers();

  return (
    <form className={classes.formContainer} onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Products and Suppliers (Optional)
      </Typography>

      <Typography color="textSecondary" paragraph>
        Select the product types and suppliers this user will work with. You can
        skip this step and configure it later.
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Product Types */}
      <Card className={classes.card} variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Product Types
          </Typography>

          {selectedProductTypes.length > 0 && (
            <Typography
              variant="body2"
              color="primary"
              className={classes.selectedCount}
            >
              {selectedProductTypes.length} product type(s) selected
            </Typography>
          )}

          <Grid container spacing={2}>
            {productTypes.map(productType => (
              <Grid item xs={12} sm={6} md={4} key={productType.id}>
                <Box className={classes.supplierItem}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedProductTypes.includes(productType.id)}
                        onChange={e =>
                          handleProductTypeChange(
                            productType.id,
                            e.target.checked
                          )
                        }
                        color="primary"
                      />
                    }
                    label={productType.name}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          {productTypes.length === 0 && (
            <Typography color="textSecondary" variant="body2">
              No product types available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Suppliers */}
      <Card className={classes.card} variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Suppliers
          </Typography>

          {selectedProductTypes.length === 0 ? (
            <Alert severity="info">
              Please select at least one product type to see available suppliers
            </Alert>
          ) : (
            <>
              {selectedSuppliers.length > 0 && (
                <Typography
                  variant="body2"
                  color="primary"
                  className={classes.selectedCount}
                >
                  {selectedSuppliers.length} supplier(s) selected
                </Typography>
              )}

              <Grid container spacing={2}>
                {availableSuppliers.map(supplier => (
                  <Grid item xs={12} sm={6} md={4} key={supplier.id}>
                    <Box className={classes.supplierItem}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSuppliers.includes(supplier.id)}
                            onChange={e =>
                              handleSupplierChange(
                                supplier.id,
                                e.target.checked
                              )
                            }
                            color="primary"
                          />
                        }
                        label={supplier.supplier_name}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {availableSuppliers.length === 0 && (
                <Typography color="textSecondary" variant="body2">
                  No suppliers available for the selected product types
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" className={classes.note}>
        <Typography variant="body2">
          <strong>Note:</strong> Product types and suppliers can be modified
          later through the user management interface.
        </Typography>
      </Alert>

      <div className={classes.buttons}>
        <Button onClick={onBack} disabled={!onBack}>
          Back
        </Button>
        <Button variant="contained" color="primary" type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
